import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR } from "@/lib/api";
import { CheckCircle2, Truck, X, Trash2, User as UserIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [open, setOpen] = useState(null);

    const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
    useEffect(() => { load(); }, []);

    const markPaid = async (id) => {
        try {
            await api.post(`/admin/orders/${id}/mark-paid`);
            toast.success("Pagamento confirmado · fatura Moloni gerada");
            load();
            if (open?.id === id) {
                const updated = await api.get(`/orders/${id}`);
                setOpen(updated.data);
            }
        } catch (e) {
            toast.error("Erro");
        }
    };

    const setTracking = async (id, code) => {
        await api.post(`/admin/orders/${id}/tracking`, { tracking_code: code });
        toast.success("Tracking actualizado");
        load();
        if (open?.id === id) {
            const updated = await api.get(`/orders/${id}`);
            setOpen(updated.data);
        }
    };

    const deleteOrder = async (id, orderNumber) => {
        if (!window.confirm(`Apagar definitivamente a encomenda ${orderNumber}?\n\nEsta acção é IRREVERSÍVEL.`)) return;
        try {
            await api.delete(`/admin/orders/${id}`);
            toast.success("Encomenda apagada");
            setOpen(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro a apagar");
        }
    };

    return (
        <div data-testid="admin-orders-page">
            <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Encomendas</h1>
            <div className="border-2 border-ink bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink text-white text-xs font-mono uppercase tracking-wider">
                        <tr>
                            <th className="text-left p-3">Nº</th>
                            <th className="text-left p-3">Cliente</th>
                            <th className="text-left p-3">Método</th>
                            <th className="text-left p-3">Estado</th>
                            <th className="text-left p-3">Tracking</th>
                            <th className="text-right p-3">Total</th>
                            <th className="text-right p-3">Acções</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((o) => (
                            <tr key={o.id} className="border-t-2 border-ink/10 hover:bg-magenta/5" data-testid={`order-row-${o.order_number}`}>
                                <td className="p-3 font-mono">
                                    <button className="hover:text-magenta" onClick={() => setOpen(o)}>
                                        {o.order_number}
                                    </button>
                                </td>
                                <td className="p-3">{o.customer.name}</td>
                                <td className="p-3 font-mono uppercase">{o.payment_method}</td>
                                <td className="p-3">
                                    <span
                                        className={`badge-edge ${
                                            o.payment_status === "paid"
                                                ? "bg-accent-green text-white border-accent-green"
                                                : "bg-paper border-ink"
                                        }`}
                                    >
                                        {o.payment_status}
                                    </span>
                                </td>
                                <td className="p-3 font-mono text-xs">{o.tracking_code || "—"}</td>
                                <td className="p-3 text-right font-mono">{fmtEUR(o.total)}</td>
                                <td className="p-3 text-right">
                                    <div className="flex gap-3 justify-end items-center">
                                        {o.payment_status !== "paid" && (
                                            <button
                                                onClick={() => markPaid(o.id)}
                                                className="text-xs font-bold uppercase tracking-wider text-accent-green hover:underline"
                                                data-testid={`mark-paid-${o.order_number}`}
                                            >
                                                <CheckCircle2 size={14} className="inline" /> Marcar pago
                                            </button>
                                        )}
                                        <button
                                            onClick={() => deleteOrder(o.id, o.order_number)}
                                            className="text-xs text-red-600 hover:underline"
                                            title="Apagar encomenda"
                                            data-testid={`delete-order-${o.order_number}`}
                                        >
                                            <Trash2 size={14} className="inline" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {orders.length === 0 && (
                            <tr>
                                <td colSpan={7} className="p-8 text-center font-mono text-ink/50">
                                    Sem encomendas.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {open && <OrderModal order={open} onClose={() => setOpen(null)} onPaid={() => markPaid(open.id)} onTracking={(c) => setTracking(open.id, c)} onDelete={() => deleteOrder(open.id, open.order_number)} />}
        </div>
    );
}

function OrderModal({ order, onClose, onPaid, onTracking, onDelete }) {
    const [code, setCode] = useState(order.tracking_code || "");
    const [linkedUser, setLinkedUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);

    useEffect(() => {
        api.get(`/admin/orders/${order.id}/customer`)
            .then((r) => setLinkedUser(r.data?.user || null))
            .catch(() => setLinkedUser(null))
            .finally(() => setLoadingUser(false));
    }, [order.id]);
    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border-2 border-ink w-full max-w-2xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex items-center justify-between">
                    <div>
                        <h2 className="font-display font-black text-2xl">{order.order_number}</h2>
                        <div className="label-mono mt-1">{new Date(order.created_at).toLocaleString("pt-PT")}</div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:text-magenta"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <div>
                        <div className="label-mono mb-1 flex items-center gap-2">
                            Cliente
                            {loadingUser ? null : linkedUser ? (
                                <Link
                                    to={`/admin/utilizadores?user=${linkedUser.id}`}
                                    onClick={onClose}
                                    className="ml-2 text-xs font-bold uppercase tracking-wider text-magenta hover:underline inline-flex items-center gap-1"
                                    data-testid="open-customer-link"
                                >
                                    <UserIcon size={12} /> Ver ficha <ExternalLink size={10} />
                                </Link>
                            ) : (
                                <span className="text-xs font-mono text-ink/40">(convidado · sem conta)</span>
                            )}
                        </div>
                        <div className="font-bold">{order.customer.name}</div>
                        <div className="text-sm font-mono">{order.customer.email} · {order.customer.phone}</div>
                        {order.customer.nif && <div className="text-sm font-mono">NIF: {order.customer.nif}</div>}
                    </div>
                    <div>
                        <div className="label-mono mb-1">Morada</div>
                        <div className="text-sm">
                            {order.customer.address}<br/>
                            {order.customer.postal_code} {order.customer.city} · {order.customer.country}
                        </div>
                    </div>
                    <div className="border-2 border-ink">
                        <table className="w-full text-sm">
                            <tbody>
                                {order.items.map((i) => (
                                    <tr key={i.sku} className="border-b border-ink/10 last:border-b-0">
                                        <td className="p-3">
                                            <div className="font-bold">{i.name}</div>
                                            <div className="text-xs font-mono text-ink/60">{i.sku} · {i.quantity}×</div>
                                        </td>
                                        <td className="p-3 text-right font-mono">{fmtEUR(i.line_total)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-paper">
                                    <td className="p-3 font-bold">Total</td>
                                    <td className="p-3 text-right font-display font-black text-xl">{fmtEUR(order.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {order.invoice && (
                        <div className="border-2 border-accent-green bg-accent-green/5 p-4">
                            <div className="label-mono text-accent-green">Fatura Moloni</div>
                            <div className="font-mono">{order.invoice.number}</div>
                        </div>
                    )}

                    <div>
                        <div className="label-mono mb-2">Tracking ({order.shipping_carrier})</div>
                        <div className="flex gap-2">
                            <input value={code} onChange={(e) => setCode(e.target.value)} className="input-edge flex-1" placeholder="Código de rastreio" />
                            <button onClick={() => onTracking(code)} className="btn-secondary">
                                <Truck size={14} /> Guardar
                            </button>
                        </div>
                    </div>

                    {order.payment_status !== "paid" && (
                        <button onClick={onPaid} className="btn-primary w-full">
                            <CheckCircle2 size={16} /> Confirmar Pagamento (gera fatura Moloni)
                        </button>
                    )}
                    <button
                        onClick={onDelete}
                        className="w-full border-2 border-red-600 text-red-600 py-3 px-4 font-bold uppercase tracking-wider text-sm hover:bg-red-600 hover:text-white transition-colors flex items-center justify-center gap-2"
                        data-testid="delete-order-modal-btn"
                    >
                        <Trash2 size={14} /> Apagar encomenda
                    </button>
                </div>
            </div>
        </div>
    );
}
